
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Check, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { isValidHex, hexToRgb, rgbToHex, convertColorFormat } from '@/utils/colorUtils';

interface ColorFieldProps {
  colorName: string;
  colorLabel: string;
  colorValue: string;
  description?: string;
  onSave: (value: string) => Promise<void>;
}

type ColorFormat = 'hex' | 'rgb' | 'hsl';

const ColorField = ({
  colorName,
  colorLabel,
  colorValue,
  description,
  onSave
}: ColorFieldProps) => {
  const [value, setValue] = useState(colorValue);
  const [format, setFormat] = useState<ColorFormat>('hex');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleFormatChange = (newFormat: ColorFormat) => {
    setFormat(newFormat);
    try {
      const converted = convertColorFormat(value, format, newFormat);
      setValue(converted);
    } catch (err) {
      toast.error('Unable to convert color format');
    }
  };

  const handleSave = async () => {
    // Always save in HEX format for consistency
    try {
      setIsSaving(true);
      const hexValue = format === 'hex' ? value : convertColorFormat(value, format, 'hex');
      
      if (!isValidHex(hexValue)) {
        toast.error('Invalid color format');
        return;
      }
      
      await onSave(hexValue);
      setIsEditing(false);
    } catch (err) {
      toast.error('Failed to save color');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <Label htmlFor={colorName}>{colorLabel}</Label>
        <Select 
          value={format} 
          onValueChange={(v) => handleFormatChange(v as ColorFormat)}
        >
          <SelectTrigger className="w-20 h-8">
            <SelectValue placeholder="Format" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hex">HEX</SelectItem>
            <SelectItem value="rgb">RGB</SelectItem>
            <SelectItem value="hsl">HSL</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex items-center gap-2">
        <div 
          className="h-8 w-8 rounded-md border" 
          style={{ backgroundColor: value }}
        />
        
        <div className="flex-1">
          <Input
            id={colorName}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setIsEditing(true);
            }}
            className="font-mono"
          />
        </div>
        
        {isEditing && (
          <Button 
            size="sm" 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
      
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
};

export default ColorField;
